import { useState } from "react";
import { useNavigate } from "react-router";

import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { getPublicSiteUrl } from "@/lib/utils/url";
import {
  Sparkles,
  Globe,
  User,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  ExternalLink,
} from "lucide-react";

export function CreateProfilePage() {
  const [step, setStep] = useState<1 | 2>(1);
  const [fullName, setFullName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [isSubdomainManual, setIsSubdomainManual] = useState(false);

  const navigate = useNavigate();
  const trpcUtils = trpc.useUtils();

  const createMutation = trpc.profile.createProfile.useMutation({
    onSuccess: async () => {
      toast.success("Profile created successfully! Welcome to Profolio.");
      await trpcUtils.profile.getMyProfile.invalidate();
      navigate("/profile/edit");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create profile");
    },
  });

  const handleNameChange = (val: string) => {
    setFullName(val);
    if (!isSubdomainManual) {
      // Auto-suggest slug from name
      const slug = val
        .toLowerCase()
        .replace(/^(dr\.|prof\.|mr\.|mrs\.|ms\.)\s+/i, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      setSubdomain(slug);
    }
  };

  const handleSubdomainChange = (val: string) => {
    setIsSubdomainManual(true);
    setSubdomain(val.toLowerCase().replace(/[^a-z0-9-]/g, ""));
  };

  const publicSiteDomain =
    import.meta.env.VITE_PUBLIC_SITE_DOMAIN &&
    import.meta.env.VITE_PUBLIC_SITE_DOMAIN !== "profolio.lk"
      ? import.meta.env.VITE_PUBLIC_SITE_DOMAIN
      : "profolio-renderer.dresser-manager.workers.dev";

  const isWorkersDev = publicSiteDomain.includes("workers.dev");

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      if (fullName.trim().length < 2) {
        toast.error("Please enter your full name");
        return;
      }
      setStep(2);
      return;
    }

    if (!subdomain || subdomain.length < 2) {
      toast.error("Please choose a valid subdomain");
      return;
    }

    createMutation.mutate({ subdomain, fullName });
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-background p-6">
      {/* Background glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md space-y-6 relative z-10">
        {/* Brand & Progress Bar */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold border border-primary/20">
            <Sparkles className="h-3.5 w-3.5" />
            <span>Academic Onboarding</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            {step === 1 ? "Let's begin with your identity" : "Claim your personal web address"}
          </h1>
          <p className="text-xs text-muted-foreground">
            {step === 1
              ? "Tell us how you would like your name and academic title to appear."
              : "Your free, permanent academic URL on Profolio's global edge network."}
          </p>

          {/* Stepper Dots */}
          <div className="flex items-center justify-center gap-2 pt-2">
            <div
              className={`h-1.5 rounded-full transition-all duration-300 ${
                step === 1 ? "w-8 bg-primary" : "w-2 bg-muted-foreground/40"
              }`}
            />
            <div
              className={`h-1.5 rounded-full transition-all duration-300 ${
                step === 2 ? "w-8 bg-primary" : "w-2 bg-muted-foreground/40"
              }`}
            />
          </div>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-border/80 bg-card p-6 md:p-8 shadow-xs">
          <form onSubmit={onSubmit} className="space-y-5">
            {step === 1 ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5 text-primary" />
                    <span>Full Name & Academic Title</span>
                  </label>
                  <Input
                    type="text"
                    required
                    autoFocus
                    value={fullName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g. Dr. Jane Doe, Ph.D."
                    className="h-10 text-xs"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    You can adjust how titles and degrees are formatted later in Content Studio.
                  </p>
                </div>

                <Button
                  type="button"
                  onClick={() => {
                    if (fullName.trim().length < 2) {
                      toast.error("Please enter your name");
                      return;
                    }
                    setStep(2);
                  }}
                  className="w-full h-10 text-xs font-semibold gap-1.5 mt-2"
                >
                  <span>Continue to Web Address</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5 text-primary" />
                    <span>Subdomain / Unique Handle</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      required
                      autoFocus
                      value={subdomain}
                      onChange={(e) => handleSubdomainChange(e.target.value)}
                      placeholder="janedoe"
                      title="Lowercase letters, numbers, and hyphens only"
                      className="h-10 text-xs font-mono"
                    />
                    <span className="text-xs font-mono text-muted-foreground whitespace-nowrap px-2 py-2 rounded-md bg-muted border border-border/60">
                      {isWorkersDev ? `(identifier)` : `.${publicSiteDomain}`}
                    </span>
                  </div>
                </div>

                {/* Live URL Preview Badge */}
                <div className="p-3 rounded-xl bg-muted/40 border border-border/80 text-xs space-y-1">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block">
                    Your Live Website URL
                  </span>
                  <div className="flex items-center gap-1.5 font-mono text-primary font-medium truncate">
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate">
                      {getPublicSiteUrl(subdomain || "yourname")}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="h-10 text-xs gap-1.5"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    <span>Back</span>
                  </Button>

                  <Button
                    type="submit"
                    disabled={createMutation.isPending || !subdomain}
                    className="flex-1 h-10 text-xs font-semibold gap-1.5"
                  >
                    {createMutation.isPending ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        <span>Launching Site...</span>
                      </>
                    ) : (
                      <>
                        <Check className="h-3.5 w-3.5" />
                        <span>Launch Portfolio</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
